import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Network, RefreshCw, UploadCloud, Link as LinkIcon, ShieldCheck, Database } from "lucide-react";

export default function AdminEdkonnect() {
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [students, setStudents] = useState([]);
  const [pushing, setPushing] = useState(null);

  const load = () => {
    api.get("/integrations/edkonnect/status").then(r => setStatus(r.data));
    api.get("/admin/users?role=student").then(r => setStudents(r.data));
  };
  useEffect(load, []);

  const fetchPreview = async () => {
    try {
      const r = await api.get("/integrations/edkonnect/sync/preview");
      setPreview(r.data);
      toast.success(r.data.users.length ? `${r.data.users.length} users available to sync` : "No users to sync (stub mode)");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const pushConsumption = async (sid) => {
    setPushing(sid);
    try {
      const r = await api.post(`/integrations/edkonnect/push/consumption/${sid}`);
      toast.success(`Pushed ${r.data.orders_count} orders for ${r.data.student}`);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setPushing(null); }
  };

  return (
    <AppShell variant="admin">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Integration · EdKonnect</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-ink mt-2">EdKonnect bridge</h1>
          <p className="text-sm text-ink-600 mt-2 max-w-2xl">Connects Grubpe to your school's EdKonnect ERP — for SSO, master-data sync of students/parents/RFIDs, and pushing meal consumption data back to the EdKonnect parent app.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
          <div className={`rounded-3xl p-6 border ${status?.configured?'bg-emerald-50 border-emerald-200':'bg-amber-50 border-amber-200'}`} data-testid="edk-status">
            <div className="flex items-center gap-2"><Network size={18} /><span className="text-xs font-bold uppercase tracking-[0.2em]">Connection</span></div>
            <div className="font-heading text-2xl font-extrabold mt-2">{status?.configured ? "Live" : "Stub mode"}</div>
            <div className="text-xs mt-1 text-ink-600">{status?.api_url || "(no API URL set)"}</div>
            {!status?.configured && <div className="mt-3 text-[11px] text-ink-700">Set <code className="font-mono bg-white/60 px-1 py-0.5 rounded">EDKONNECT_API_URL</code> + <code className="font-mono bg-white/60 px-1 py-0.5 rounded">EDKONNECT_API_KEY</code> in backend env to go live.</div>}
          </div>
          <div className="rounded-3xl bg-white p-6 border border-border">
            <div className="flex items-center gap-2 text-ink-500"><ShieldCheck size={18} /><span className="text-xs font-bold uppercase tracking-[0.2em]">SSO</span></div>
            <div className="font-heading text-2xl font-extrabold mt-2 text-ink">Single sign-on</div>
            <div className="text-xs mt-1 text-ink-600">Endpoint: <span className="font-mono">POST /api/integrations/edkonnect/sso/login</span></div>
            <div className="text-[11px] text-ink-500 mt-2">In stub mode, tokens starting with <code className="font-mono bg-cream-200 px-1 py-0.5 rounded">edk_demo_</code> are accepted.</div>
          </div>
          <div className="rounded-3xl bg-ink text-white p-6">
            <div className="flex items-center gap-2 text-white/70"><Database size={18} /><span className="text-xs font-bold uppercase tracking-[0.2em] text-sun">Sync</span></div>
            <div className="font-heading text-2xl font-extrabold mt-2">Master data</div>
            <button data-testid="edk-preview" onClick={fetchPreview} className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white text-sm font-semibold hover:bg-white/20"><RefreshCw size={14} /> Preview sync</button>
            {preview && <div className="mt-3 text-xs text-white/70">{preview.users.length} users available · {preview.configured ? "live" : "stub"}</div>}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-border overflow-x-auto">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <div className="font-heading text-xl font-bold flex items-center gap-2"><UploadCloud size={18} /> Push consumption to EdKonnect</div>
            <div className="text-xs text-ink-500">Sends each student's meal history to their EdKonnect parent app.</div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-cream-200">
              <tr>
                <th className="p-3 text-left text-xs uppercase font-bold">Student</th>
                <th className="p-3 text-left text-xs uppercase font-bold">Grade</th>
                <th className="p-3 text-left text-xs uppercase font-bold">EdK link</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {students.map(s => (
                <tr key={s.id} className="border-t border-border">
                  <td className="p-3 font-semibold">{s.name}</td>
                  <td className="p-3">{s.grade}</td>
                  <td className="p-3 font-mono text-xs text-ink-500">{s.edk_user_id || "— not linked —"}</td>
                  <td className="p-3 text-right">
                    <button data-testid={`edk-push-${s.id}`} disabled={pushing===s.id} onClick={() => pushConsumption(s.id)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-ink text-white text-xs font-bold disabled:opacity-50 hover:bg-ink-700">
                      <LinkIcon size={12} /> {pushing===s.id ? "Pushing..." : "Push"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 bg-cream-200 rounded-3xl p-6">
          <div className="font-heading text-lg font-bold text-ink">Endpoint contract for your EdKonnect team</div>
          <pre className="mt-3 bg-white rounded-xl p-4 text-xs font-mono text-ink-700 overflow-x-auto border border-border">{`POST /api/integrations/edkonnect/sso/login
  body: { edk_token: "<jwt issued by EdKonnect>" }
  → { token, user, via: "edkonnect" }

POST /api/integrations/edkonnect/sync/users   (admin)
  body: { users: [{ edk_user_id, email, name, role, grade?, parent_edk_id?, rfid_card_id? }] }
  → { inserted, updated, total }

POST /api/integrations/edkonnect/push/consumption/{student_id}   (admin)
  → { pushed: bool, student, orders_count }`}</pre>
        </div>
      </div>
    </AppShell>
  );
}
